import React from 'react';
import { X, User, Building, Mail, Phone, Calendar, Clock, Cake, Briefcase, Hash, Send, Activity, History, Edit2, Link2, KeyRound, Ban, PlayCircle } from 'lucide-react';
import { StaffAvatar } from './StaffAvatar';
import { RolePill, StatusPills } from './StaffBadges';
import { formatDate, relativeTime } from './peopleUtils';

const Info = ({ Icon, label, value }) => (
  <div className="p-3 rounded-xl bg-white/[0.02] border border-white/[0.06]">
    <p className="flex items-center gap-1.5 text-[9px] text-white/40 uppercase tracking-[0.16em] mb-1"><Icon className="w-3 h-3" />{label}</p>
    <p className="text-sm text-white/90 truncate">{value || '—'}</p>
  </div>
);

const Action = ({ Icon, label, onClick, testId, hex = '#00E5FF' }) => (
  <button onClick={onClick} data-testid={testId}
    className="flex flex-col items-center gap-1.5 py-3 rounded-xl border border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.05] transition-colors">
    <Icon className="w-4 h-4" style={{ color: hex }} /><span className="text-[10px] text-white/70">{label}</span>
  </button>
);

const TagEditor = ({ tags, available, onAdd, onRemove }) => (
  <div className="p-3 rounded-xl bg-white/[0.02] border border-white/[0.06]" data-testid="tag-management">
    <p className="flex items-center gap-1.5 text-[9px] text-white/40 uppercase tracking-[0.16em] mb-2"><Hash className="w-3 h-3" />Account tags</p>
    <div className="flex flex-wrap gap-1.5 mb-2">
      {tags.length === 0 ? <p className="text-white/30 text-xs">No tags assigned</p> : tags.map(t => (
        <span key={t} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] bg-violet-500/15 text-violet-200 border border-violet-400/25">
          {t}<button onClick={() => onRemove(t)} data-testid={`remove-tag-${t}`} className="hover:text-rose-300"><X className="w-3 h-3" /></button>
        </span>
      ))}
    </div>
    <div className="flex flex-wrap gap-1">
      {available.filter(t => !tags.includes(t)).map(t => (
        <button key={t} onClick={() => onAdd(t)} data-testid={`add-tag-${t}`}
          className="px-2 py-0.5 rounded-full text-[10px] text-white/45 border border-white/[0.08] hover:text-cyan-200 hover:border-cyan-400/30 transition-colors">+ {t}</button>
      ))}
    </div>
  </div>
);

export const StaffQuickViewDrawer = ({ staff, onClose, online, onLeave, probation, isSelf, actions, tagProps }) => (
  <div className="fixed inset-0 z-50 flex justify-end" data-testid="staff-quick-view">
    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
    <aside className="relative w-full max-w-md h-full overflow-y-auto bg-[#08080f] border-l border-white/[0.08] p-6 space-y-5 animate-[slideInRight_0.3s_ease-out]">
      <div className="pointer-events-none absolute -top-24 -right-24 w-72 h-72 rounded-full blur-[110px] bg-cyan-500/20" />
      <button onClick={onClose} data-testid="close-quick-view" className="absolute top-4 right-4 p-1.5 rounded-lg text-white/40 hover:text-white hover:bg-white/[0.06]"><X className="w-4 h-4" /></button>
      <div className="relative flex items-center gap-4">
        <StaffAvatar staff={staff} size="w-16 h-16" online={online} />
        <div className="min-w-0">
          <p className="text-[9px] tracking-[0.25em] uppercase text-cyan-300/70">Quick view</p>
          <h3 className="text-xl text-white font-semibold truncate" data-testid="quick-view-name">{staff.display_name || staff.username}</h3>
          <p className="text-white/45 text-xs">@{staff.username}{staff.job_title ? ` · ${staff.job_title}` : ''}</p>
        </div>
      </div>
      <div className="relative flex flex-wrap gap-1.5"><RolePill role={staff.role} /><StatusPills staff={staff} onLeave={!!onLeave} probation={probation} /></div>
      {onLeave && <p className="text-[11px] text-sky-300/80 -mt-2">On {onLeave.leave_type} leave until {formatDate(onLeave.end_date)}</p>}
      <div className="relative grid grid-cols-4 gap-2">
        <Action Icon={Send} label="Message" onClick={() => actions.message(staff)} testId="qv-message" />
        <Action Icon={Activity} label="HR profile" onClick={() => actions.hrProfile(staff)} testId="qv-hr-profile" hex="#10B981" />
        <Action Icon={History} label="Action log" onClick={() => actions.actionLog(staff)} testId="qv-action-log" hex="#A78BFA" />
        <Action Icon={Edit2} label="Edit" onClick={() => actions.edit(staff)} testId="qv-edit" hex="#F59E0B" />
        <Action Icon={Link2} label="Invite link" onClick={() => actions.invite(staff)} testId="qv-invite" hex="#8A2BE2" />
        <Action Icon={KeyRound} label="Password" onClick={() => actions.reset(staff)} testId="qv-reset" hex="#F59E0B" />
        {!isSelf && !staff.is_protected && (staff.is_suspended
          ? <Action Icon={PlayCircle} label="Reactivate" onClick={() => actions.unsuspend(staff)} testId="qv-unsuspend" hex="#10B981" />
          : <Action Icon={Ban} label="Suspend" onClick={() => actions.suspend(staff)} testId="qv-suspend" hex="#F43F5E" />)}
      </div>
      <div className="relative grid grid-cols-2 gap-2">
        <Info Icon={User} label="Username" value={`@${staff.username}`} />
        <Info Icon={Building} label="Department" value={staff.department} />
        <Info Icon={Mail} label="Email" value={staff.email} />
        <Info Icon={Phone} label="Phone" value={staff.phone} />
        <Info Icon={Briefcase} label="Hire date" value={staff.hire_date ? formatDate(staff.hire_date) : null} />
        <Info Icon={Cake} label="Birthday" value={staff.date_of_birth ? new Date(staff.date_of_birth).toLocaleDateString('en-GB', { day: 'numeric', month: 'long' }) : null} />
        <Info Icon={Calendar} label="Account created" value={formatDate(staff.created_at)} />
        <Info Icon={Clock} label="Last active" value={relativeTime(staff.last_login)} />
      </div>
      {staff.is_suspended && (
        <div className="relative p-3 rounded-xl bg-rose-500/[0.06] border border-rose-400/25 text-xs text-rose-200">
          Suspended by {staff.suspended_by || 'admin'}{staff.suspended_at ? ` · ${formatDate(staff.suspended_at)}` : ''}{staff.suspend_reason ? ` — ${staff.suspend_reason}` : ''}
        </div>
      )}
      {tagProps && <TagEditor tags={staff.tags || []} {...tagProps} />}
    </aside>
  </div>
);
